import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaUpload } from "react-icons/fa";
import { IoIosCloseCircle } from "react-icons/io";
import FolderManager from "./folderManager";
import {
  uploadImage,
  updateImage,
  fetchFolderData,
} from "../../Slices/imageGallery/uploadImage.slice";
import { UploadProgressBar } from "components/shared/loader";

const ImageUploadForm = ({
  setImageUpload,
  openedFolderID,
  selectedImage = null,
  isEditMode = false,
}) => {
  const dispatch = useDispatch();
  const uploadProgress = useSelector((state) => state.uploadImage.uploadProgress);
  const [imageData, setImageData] = useState({
    file: null,
    title: "",
    alt: "",
    photographer: "",
    instagram: "",
    facebook: "",
    youtube: "",
  });

  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [compressImage, setCompressImage] = useState(false);
  const [hideToggle, setHideToggle] = useState(false);
  const [heicMessage, setHeicMessage] = useState("");




  useEffect(() => {
    if (isEditMode && selectedImage) {
      console.log("🖼️ Fetching Image Data for Editing:", selectedImage);

      setImageData({
        file: null,
        title: selectedImage.title || "",
        alt: selectedImage.alt || "",
        photographer: selectedImage.photographerName || "",
        instagram: selectedImage.photographerSocialLinks?.instagram || "",
        facebook: selectedImage.photographerSocialLinks?.facebook || "",
        youtube: selectedImage.photographerSocialLinks?.youtube || "",
      });
    }
  }, [isEditMode, selectedImage]);



  const handleChange = (e) => {
    const { name, type, files, value } = e.target;

    if (type === "file" && files.length > 0) {
      const file = files[0];
      const fileType = file.name.split(".").pop().toLowerCase();

      // ✅ Show HEIC warning but keep the toggle visible
      if (fileType === "heic") {
        setHeicMessage("⚠️ HEIC images will be automatically converted to JPG. Compression is optional.");
      } else {
        setHeicMessage(""); // ✅ Reset message
      }

      setImageData((prevData) => ({
        ...prevData,
        file: file,
      }));
    } else {
      setImageData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!imageData.file && !isEditMode) {
      alert("Please select an image file to upload.");
      return;
    }

    setIsUploading(true);
    setProgress(0);

    if (isEditMode && selectedImage) {
      const updatedImageData = {
        title: imageData.title || selectedImage.title,
        alt: imageData.alt || selectedImage.alt,
        photographerName: imageData.photographer || selectedImage.photographerName,
        photographerSocialLinks: {
          instagram: imageData.instagram || selectedImage.photographerSocialLinks?.instagram || "",
          facebook: imageData.facebook || selectedImage.photographerSocialLinks?.facebook || "",
          youtube: imageData.youtube || selectedImage.photographerSocialLinks?.youtube || "",
        },
      };

      console.log("📤 Updated Image Data in Form:", updatedImageData);

      dispatch(updateImage({
        folderID: openedFolderID,
        imageID: selectedImage._id,
        imageData: updatedImageData,
      }))
        .unwrap()
        .then((updatedImage) => {
          console.log("✅ Image metadata updated successfully!", updatedImage);
          dispatch(fetchFolderData(openedFolderID)); // ✅ Refresh images immediately
          setUploadComplete(true);
          setIsUploading(false);
          setImageUpload(false);
        })
        .catch((error) => {
          console.error("❌ Error updating image metadata:", error);
          setIsUploading(false);
        });
    } else {
      // ✅ Upload New Image with Correct Compression Toggle
      const formData = new FormData();
      formData.append("file", imageData.file);
      formData.append("compress", compressImage ? "true" : "false"); // ✅ Proper compression flag
      formData.append("title", imageData.title);
      formData.append("alt", imageData.alt);
      formData.append("photographerName", imageData.photographer);
      formData.append("photographerSocialLinks", JSON.stringify({
        instagram: imageData.instagram,
        facebook: imageData.facebook,
        youtube: imageData.youtube,
      }));

      console.log("📤 Uploading New Image:", [...formData.entries()]); // ✅ Debugging FormData

      dispatch(uploadImage({
        formData,
        folderID: openedFolderID,
      }))
        .unwrap()
        .then((uploadedImage) => {
          console.log("✅ Image Uploaded Successfully:", uploadedImage);
          dispatch(fetchFolderData(openedFolderID)); // ✅ Refresh images immediately
          setUploadComplete(true);
          setIsUploading(false);
          setImageUpload(false);
        })
        .catch((error) => {
          console.error("❌ Error uploading image:", error);
          setIsUploading(false);
        });
    }
  };






  // ✅ Handle closing the popup
  const handleClosePopup = () => {
    if (!isUploading) setImageUpload(false);
  };

  return (
    <section id="imageUploadForm">
      <form onSubmit={handleSubmit}>
        <fieldset>
          <div className="row align-items-center">
            <legend>{isEditMode ? "Update Image" : "Upload Image"}</legend>
            <IoIosCloseCircle className="icon-24" onClick={handleClosePopup} />
          </div>
          <div className="inputWrapper">
            <input
              type="file"
              name="file"
              id="imageFile"
              onChange={handleChange}
              disabled={isEditMode}
            />
          </div>

          <div className="toggleWrapper">
            <span>No Compress</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={compressImage}
                onChange={() => setCompressImage((prev) => !prev)}
              />
              <span className="slider round"></span>
            </label>
            <span>Compress Image</span>
          </div>


          <div className="inputWrapper">
            <label htmlFor="title">Image Title</label>
            <input
              type="text"
              id="title"
              name="title"
              value={imageData.title}
              onChange={handleChange}
              placeholder="Enter image title"
              required
            />
          </div>
          <div className="inputWrapper">
            <label htmlFor="alt">Alt Text</label>
            <input
              type="text"
              id="alt"
              name="alt"
              value={imageData.alt}
              onChange={handleChange}
              placeholder="Enter alt text"
            />
          </div>
          <div className="inputWrapper">
            <label htmlFor="photographer">Photographer Name</label>
            <input
              type="text"
              id="photographer"
              name="photographer"
              value={imageData.photographer}
              onChange={handleChange}
              placeholder="Photographer name"
            />
          </div>
          <div className="inputWrapper">
            <input
              type="text"
              name="instagram"
              value={imageData.instagram}
              onChange={handleChange}
              placeholder="Instagram"
            />
            <input
              type="text"
              name="facebook"
              value={imageData.facebook}
              onChange={handleChange}
              placeholder="Facebook"
            />
            <input
              type="text"
              name="youtube"
              value={imageData.youtube}
              onChange={handleChange}
              placeholder="YouTube"
            />
          </div>
        </fieldset>
        <button type="submit" disabled={isUploading || uploadComplete}>
          <FaUpload />
          {isUploading ? "Uploading..." : isEditMode ? "Update Image" : "Upload Image"}
        </button>
      </form>
      {isUploading && <UploadProgressBar progress={uploadProgress} />}

      {uploadComplete && (
        <div>
          <p style={{ color: "green" }}>
            {isEditMode ? "Image updated successfully!" : "Image uploaded successfully!"}
          </p>
          <button onClick={() => setImageUpload(false)}>Done</button>
        </div>
      )}
    </section>
  );
};

export default ImageUploadForm;


const UploadImages = () => {
  return (
    <>
      <FolderManager />
    </>
  );
};

export { UploadImages, ImageUploadForm };
